import styled from '@emotion/styled';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

import {
  ORG_ADMIN_TAB_CAPABILITY,
  ORG_ADMIN_TAB_ENTRY_CAPABILITIES,
  ORG_ADMIN_TAB_LABELS,
  PERSONAL_SETTINGS_TAB_LABELS,
  SYSTEM_ADMIN_TAB_LABELS,
} from '../admin/tab-constants';
import { ORG_ICONS, PERSONAL_ICONS, SYS_ICONS } from '../admin/tab-icons';
import Arranger from '../base/arranger';
import Chip from '../base/chip';
import { LogOutDoor, UserProfileCircle } from '../base/icon';
import Spacer from '../base/spacer';
import { CapabilityAuth } from '../capability-auth';

import { DesignToken } from '~/client/components/base/core';
import { useThemeMode } from '~/client/contexts/theme-context';
import { useUserProfile } from '~/client/hooks/user-profile';
import { dashboardRouteUtil, settingsRoutesUtil } from '~/client/routes';
import { nodeAuthApi } from '~/client/services';
import * as GQL from '~/graphql/generated/client';
import {
  LeftNavFlyoutMenuItem,
  LeftNavMenu,
  LeftNavMenuGroup,
  LeftNavMenuItem,
  useLeftNavContext,
} from '~c/components/base/left-nav';

interface Props {
  className?: string;
}

const SettingsNavImpl: React.FC<Props> = ({ className }) => {
  const { userProfile } = useUserProfile();
  const { mode } = useThemeMode();
  const appearanceLabel =
    mode === 'system' ? 'Automatic' : mode === 'dark' ? 'Dark' : 'Light';
  const { otpEnabled } = userProfile;
  const [signOut] = nodeAuthApi.useLogoutMutation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const { data } = GQL.useUserOrganizationContextsQuery({
    skip: !userProfile.emailVerified,
    fetchPolicy: 'cache-and-network',
  });

  const defaultOrgContext = data?.userOrganizationContexts?.find(
    (membership: any) =>
      membership.status === 'ACTIVE' &&
      membership.organization?._id === userProfile.defaultOrganizationId
  );

  const orgId = defaultOrgContext?.organization?._id ?? '';

  // Collapsible section state
  const isOnOrgAdmin = location.pathname.includes('/org-admin/');
  const isOnSystemAdmin = location.pathname.includes('/system-admin');
  const isOnPersonalSettings = location.pathname.includes('/settings/personal');

  const [expandedSection, setExpandedSection] = useState<
    'personal' | 'org-admin' | 'sys' | null
  >(isOnOrgAdmin ? 'org-admin' : isOnSystemAdmin ? 'sys' : 'personal');

  const orgAdminTabIndices = React.useMemo(
    () =>
      Object.keys(ORG_ADMIN_TAB_LABELS)
        .map((k) => parseInt(k, 10))
        .sort((a, b) => a - b),
    []
  );

  const activeTabParam = searchParams.get('tab');

  const isPersonalTabActive = (tabIndex: number) =>
    isOnPersonalSettings &&
    (activeTabParam === String(tabIndex) ||
      (activeTabParam === null && tabIndex === 0));

  const isOrgTabActive = (tabIndex: number) =>
    isOnOrgAdmin && activeTabParam === String(tabIndex);

  const isSysTabActive = (tabIndex: number) =>
    isOnSystemAdmin && activeTabParam === String(tabIndex);

  const navigateToPersonalTab = (tabIndex: number) => {
    navigate(`/settings/personal?tab=${tabIndex}`);
  };

  const navigateToOrgTab = (tabIndex: number) => {
    navigate(`/org-admin/${orgId}/dashboard?tab=${tabIndex}`);
  };

  const navigateToSysTab = (tabIndex: number) => {
    navigate(`/settings/system-admin?tab=${tabIndex}`);
  };

  const { jumpTo, state } = useLeftNavContext();
  const dashboard = dashboardRouteUtil.useHelper();
  const name = settingsRoutesUtil.routePrefix;

  React.useEffect(() => {
    const isOnSettings =
      location.pathname.startsWith('/settings') ||
      location.pathname.startsWith('/org-admin');
    if (isOnSettings && state.currentRoute !== name) {
      jumpTo(name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <LeftNavMenu
      className={className}
      name={name}
      header={{
        showBackButton: true,
        showOrgSwitcher: true,
        onClick: () => dashboard.navigateTo('COMPLIANCE'),
      }}
    >
      {/* Personal Section */}
      <LeftNavMenuGroup
        icon={PersonOutlinedIcon}
        title="Personal"
        expandedIcon={ExpandLessIcon}
        collapsedIcon={ExpandMoreIcon}
        expanded={expandedSection === 'personal'}
        onToggle={() =>
          setExpandedSection(expandedSection === 'personal' ? null : 'personal')
        }
      >
        {Object.entries(PERSONAL_SETTINGS_TAB_LABELS).map(
          ([indexStr, label]) => {
            const tabIndex = parseInt(indexStr, 10);
            return (
              <LeftNavFlyoutMenuItem
                key={tabIndex}
                icon={PERSONAL_ICONS[tabIndex]}
                selected={isPersonalTabActive(tabIndex)}
                onClick={() => navigateToPersonalTab(tabIndex)}
              >
                {tabIndex === 0 ? (
                  <Arranger justify="space-between" alignItems="center">
                    {label}
                    <Chip
                      label={appearanceLabel}
                      variant="filled"
                      size="small"
                      color="default"
                    />
                  </Arranger>
                ) : tabIndex === 3 ? (
                  <Arranger justify="space-between" alignItems="center">
                    {label}
                    <Chip
                      label={otpEnabled ? 'Enabled' : 'Disabled'}
                      variant="filled"
                      color={otpEnabled ? 'success' : 'default'}
                    />
                  </Arranger>
                ) : (
                  label
                )}
              </LeftNavFlyoutMenuItem>
            );
          }
        )}
      </LeftNavMenuGroup>

      {/* Organization Admin — gated by any org-admin capability */}
      <CapabilityAuth anyCapability={ORG_ADMIN_TAB_ENTRY_CAPABILITIES}>
        <LeftNavMenuGroup
          icon={BusinessOutlinedIcon}
          title="Organization administration"
          expandedIcon={ExpandLessIcon}
          collapsedIcon={ExpandMoreIcon}
          expanded={expandedSection === 'org-admin'}
          onToggle={() =>
            setExpandedSection(
              expandedSection === 'org-admin' ? null : 'org-admin'
            )
          }
        >
          {orgAdminTabIndices.map((tabIndex) => {
            const cap = ORG_ADMIN_TAB_CAPABILITY[tabIndex];
            const item = (
              <LeftNavFlyoutMenuItem
                key={tabIndex}
                icon={ORG_ICONS[tabIndex]}
                selected={isOrgTabActive(tabIndex)}
                onClick={() => navigateToOrgTab(tabIndex)}
              >
                {ORG_ADMIN_TAB_LABELS[tabIndex]}
              </LeftNavFlyoutMenuItem>
            );
            return cap ? (
              <CapabilityAuth key={tabIndex} capability={cap}>
                {item}
              </CapabilityAuth>
            ) : (
              item
            );
          })}
        </LeftNavMenuGroup>
      </CapabilityAuth>

      {/* System administration — sub-items match system admin dashboard tabs */}
      <CapabilityAuth capability="platform:manage_organizations">
        <LeftNavMenuGroup
          icon={AdminPanelSettingsIcon}
          title="System administration"
          expandedIcon={ExpandLessIcon}
          collapsedIcon={ExpandMoreIcon}
          expanded={expandedSection === 'sys'}
          onToggle={() =>
            setExpandedSection(expandedSection === 'sys' ? null : 'sys')
          }
        >
          {Object.entries(SYSTEM_ADMIN_TAB_LABELS).map(([indexStr, label]) => {
            const tabIndex = parseInt(indexStr, 10);
            return (
              <LeftNavFlyoutMenuItem
                key={tabIndex}
                icon={SYS_ICONS[tabIndex]}
                selected={isSysTabActive(tabIndex)}
                onClick={() => navigateToSysTab(tabIndex)}
              >
                {label}
              </LeftNavFlyoutMenuItem>
            );
          })}
        </LeftNavMenuGroup>
      </CapabilityAuth>

      <Spacer className="grow" />
      <LeftNavMenuItem
        onClick={(event: any) => signOut()}
        icon={UserProfileCircle}
        rightIcon={LogOutDoor}
      >
        Log out
      </LeftNavMenuItem>
    </LeftNavMenu>
  );
};

const SettingsNav = styled(SettingsNavImpl)`
  .grow {
    flex-grow: 1;
  }
  .userinfo {
    padding: 16px;
    text-transform: capitalize;
  }
  .task-menu-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .task-badge {
    background-color: ${DesignToken.status.danger};
    color: ${DesignToken.brand.primaryContrast};
    border-radius: ${DesignToken.radiusPill}px;
    padding: 2px 8px;
    font-size: 12px;
    margin-left: 8px;
    font-weight: 500;
  }

  .v2-header {
    margin-left: -10px;
  }
`;

export default SettingsNav;
